import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { CalendarDays } from "lucide-react";
import { DateRangeFilter } from "../../components/common/DateRangeFilter";
import useFinancialsData from "../../hooks/useFinancialsData";

import GeneralReportTab from "../../components/financials/GeneralReportTab";
import TechnicianReportTab from "../../components/financials/TechnicianReportTab";
import JobSourceReportTab from "../../components/financials/JobSourceReportTab";
import DirectJobsReportTab from "../../components/financials/DirectJobsReportTab";
import TaxReportTab from "../../components/financials/TaxReportTab";
import SettlementsReportTab from "../../components/financials/SettlementsReportTab";
import SettleDialog from "../../components/financials/SettleDialog";
import FinancialsJobPreview from "../../components/financials/FinancialsJobPreview";

// Single source of truth for the tab list — both the mobile dropdown and
// the desktop TabsList render from this. Keeps labels in sync if we ever
// rename one.
const FINANCIALS_TABS = [
  { value: "general", label: "General" },
  { value: "technician", label: "Technician" },
  { value: "job-source", label: "Job Source" },
  { value: "direct", label: "Direct Jobs" },
  { value: "tax", label: "Tax" },
  { value: "settlements", label: "Settlements" },
];

const FinancialsPage = () => {
  const d = useFinancialsData();
  // Job details overlay (opened from the Technician report) — renders over
  // the report so filters / scroll position are preserved.
  const [previewJobId, setPreviewJobId] = useState(null);

  return (
    <div className="space-y-6" data-testid="financials-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground font-['Manrope']">Financial Reports</h2>
          <p className="text-muted-foreground">Detailed financial breakdown and analysis</p>
        </div>
        <div className="flex items-start gap-3 flex-wrap">
          {/* Per-location filter — only renders for accounts with 2+ locations.
              "All locations" is the default so single-location accounts and
              users who never multi-location see no extra clutter. */}
          {(d.storeLocations?.length || 0) >= 2 && (
            <Select value={d.selectedLocation} onValueChange={d.setSelectedLocation}>
              <SelectTrigger
                className="w-[170px] h-10 bg-theme-surface border-theme text-foreground"
                data-testid="financials-location-filter"
              >
                <SelectValue placeholder="All locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {d.storeLocations.map(loc => (
                  <SelectItem key={loc.location_id} value={loc.location_id} data-testid={`financials-location-opt-${loc.location_id}`}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <CalendarDays className="w-4 h-4 text-muted-foreground mt-2" />
          <DateRangeFilter
            value={d.dateFilter}
            onChange={d.setDateFilter}
            customStart={d.customStart}
            customEnd={d.customEnd}
            onCustomChange={({ start, end }) => { d.setCustomStart(start); d.setCustomEnd(end); }}
            triggerClassName="w-[160px] bg-theme-surface border-theme text-foreground"
            dataTestId="date-filter"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={d.activeTab} onValueChange={d.setActiveTab} className="w-full">
        {/* Mobile: compact "active tab pill" dropdown. Looks like the
            active tab chip + chevron — taps open a sheet with all options.
            Way nicer on phones than a 5-pill horizontal scroller or a
            wrapped grid. Hidden on sm+. */}
        <div className="sm:hidden" data-testid="financials-tabs-mobile">
          <Select value={d.activeTab} onValueChange={d.setActiveTab}>
            <SelectTrigger
              className="h-11 w-full bg-[#0046BE] hover:bg-[#0046BE]/95 border-0 text-white font-semibold rounded-xl shadow-sm focus:ring-2 focus:ring-[#0046BE]/40 [&_svg]:text-white"
              data-testid="financials-mobile-tab-select"
            >
              <SelectValue placeholder="Select report" />
            </SelectTrigger>
            <SelectContent>
              {FINANCIALS_TABS.map(t => (
                <SelectItem
                  key={t.value}
                  value={t.value}
                  data-testid={`financials-mobile-tab-opt-${t.value}`}
                >
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tablet/desktop: original pill row, unchanged behavior. */}
        <TabsList
          className="hidden sm:inline-flex bg-theme-surface border border-theme p-1 w-auto flex-wrap"
          data-testid="financials-tablist"
        >
          {FINANCIALS_TABS.map(t => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className="data-[state=active]:bg-[#0046BE] data-[state=active]:text-white text-muted-foreground"
              data-testid={`tab-${t.value}`}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {d.loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Loading report data...</div>
          </div>
        )}

        {!d.loading && (
          <>
            <TabsContent value="general" className="mt-6">
              <GeneralReportTab
                financialData={d.financialData}
                expensesSummary={d.expensesSummary}
                techBalancesGeneral={d.techBalancesGeneral}
                formatCurrency={d.formatCurrency}
                formatDate={d.formatDate}
                exportToCSV={d.exportToCSV}
                dateFilter={d.dateFilter}
                customStart={d.customStart}
                customEnd={d.customEnd}
              />
            </TabsContent>

            <TabsContent value="technician" className="mt-6">
              <TechnicianReportTab
                technicianData={d.technicianData} selectedTechnician={d.selectedTechnician} setSelectedTechnician={d.setSelectedTechnician}
                formatCurrency={d.formatCurrency} formatDate={d.formatDate} exportToPDF={d.exportToPDF} exportToCSV={d.exportToCSV}
                exportingPdf={d.exportingPdf} openSettle={d.openSettle}
                emailTechnicianReportPdf={d.emailTechnicianReportPdf} dateFilter={d.dateFilter}
                selectedJobKeys={d.selectedJobKeys} setSelectedJobKeys={d.setSelectedJobKeys}
                openSettleForJobs={d.openSettleForJobs} jobEntryBalance={d.jobEntryBalance}
                onOpenJob={(job) => setPreviewJobId(job.job_id)}
              />
            </TabsContent>

            <TabsContent value="job-source" className="mt-6">
              <JobSourceReportTab
                jobSourceData={d.jobSourceData} sourceWeeklyData={d.sourceWeeklyData}
                selectedSource={d.selectedSource} setSelectedSource={d.setSelectedSource}
                formatCurrency={d.formatCurrency} formatDate={d.formatDate} exportToPDF={d.exportToPDF} exportToCSV={d.exportToCSV}
                exportingPdf={d.exportingPdf} fetchReportData={d.fetchReportData}
              />
            </TabsContent>

            <TabsContent value="direct" className="mt-6">
              <DirectJobsReportTab directJobsData={d.directJobsData} formatCurrency={d.formatCurrency} formatDate={d.formatDate} exportToCSV={d.exportToCSV} />
            </TabsContent>

            <TabsContent value="tax" className="mt-6">
              <TaxReportTab taxData={d.taxData} formatCurrency={d.formatCurrency} formatDate={d.formatDate} exportToCSV={d.exportToCSV} exportToPDF={d.exportToPDF} exportingPdf={d.exportingPdf} />
            </TabsContent>

            <TabsContent value="settlements" className="mt-6">
              <SettlementsReportTab
                settlementsData={d.settlementsData} dateFilter={d.dateFilter}
                selectedSettleTech={d.selectedSettleTech} setSelectedSettleTech={d.setSelectedSettleTech}
                expandedWeeks={d.expandedWeeks} setExpandedWeeks={d.setExpandedWeeks}
                quickTech={d.quickTech} setQuickTech={d.setQuickTech}
                quickAmount={d.quickAmount} setQuickAmount={d.setQuickAmount}
                quickSign={d.quickSign} setQuickSign={d.setQuickSign}
                quickNotes={d.quickNotes} setQuickNotes={d.setQuickNotes}
                quickLoading={d.quickLoading} fetchReportData={d.fetchReportData}
                formatCurrency={d.formatCurrency} formatDate={d.formatDate}
              />
            </TabsContent>
          </>
        )}
      </Tabs>

      <SettleDialog
        settleOpen={d.settleOpen} setSettleOpen={d.setSettleOpen} settleTech={d.settleTech}
        formatCurrency={d.formatCurrency} formatDate={d.formatDate}
        settleAmount={d.settleAmount} setSettleAmount={d.setSettleAmount}
        settleNotes={d.settleNotes} setSettleNotes={d.setSettleNotes}
        settleHistory={d.settleHistory} settleLoading={d.settleLoading}
        settleSign={d.settleSign} setSettleSign={d.setSettleSign}
        submitSettle={d.submitSettle} deleteSettlement={d.deleteSettlement}
        settleTab={d.settleTab} setSettleTab={d.setSettleTab}
        adjAmount={d.adjAmount} setAdjAmount={d.setAdjAmount}
        adjCategory={d.adjCategory} setAdjCategory={d.setAdjCategory}
        adjNotes={d.adjNotes} setAdjNotes={d.setAdjNotes}
        adjDate={d.adjDate} setAdjDate={d.setAdjDate}
        adjHistory={d.adjHistory} adjLoading={d.adjLoading}
        adjSign={d.adjSign} setAdjSign={d.setAdjSign}
        editingAdj={d.editingAdj} submitAdjustment={d.submitAdjustment}
        startEditAdj={d.startEditAdj} cancelEditAdj={d.cancelEditAdj}
        deleteAdjustment={d.deleteAdjustment}
        settleJobs={d.settleJobs} dateFilter={d.dateFilter} jobEntryBalance={d.jobEntryBalance}
      />

      {/* Job details overlay — preserves report state (no navigation) */}
      <FinancialsJobPreview
        jobId={previewJobId}
        onClose={() => setPreviewJobId(null)}
        onChanged={d.fetchReportData}
      />
    </div>
  );
};

export default FinancialsPage;
